/**
 * ILO cluster hierarchy — fetch + cache for RENDERING the drill UI.
 *
 * `ilo_clusters` stores one row per node: leaves carry the event
 * assignments (via ilo_event_clusters), internal/meta nodes only have
 * `meta_cluster_id` back-pointers from their children. The analytics
 * schema exposes the parent pointer (META_CLUSTER_ID) and depth
 * (HIERARCHY_LEVEL) but not `is_leaf` — leaf-ness is derived here: a
 * node is a leaf iff no other node names it as parent.
 *
 * Two shapes exist in the wild (same detection as dashboard-legacy's
 * isLeidenHierShape):
 *   - flat (low-data kmeans, and older flat generations): leaves at
 *     level 0, optional meta nodes
 *     at level 1. Listed flat; metas hidden. `isHierShape === false`.
 *   - hierarchical (bisecting): internal nodes AT level 0, leaves at
 *     any depth. Explore lists roots and drills children recursively.
 *
 * SCOPE no longer expands here. Event assignments are leaf-only, so
 * scoping into an internal node used to require the client to rewrite
 * its id to the full leaf-descendant set (and chunk it under the 1024
 * IN cap). That moved server-side: the planner's virtual `cluster_subtree`
 * field resolves descendants from `meta_cluster_id` directly (O-1623),
 * so the dashboard just emits `cluster_subtree = <node id>` (see
 * buildPromptRootAndFilter in analyticsClient.js). This module now exists
 * purely to render the hierarchy — list roots, drill children, tell a
 * leaf from an internal node — not to rewrite queries.
 *
 * The tree is shared through runQuery and respects the generation pin.
 * A pin change or an unpinned picker opening resets the cache
 * (resetClusterTree, wired below), and the next caller refetches.
 * Load failures degrade to an empty tree: the
 * cluster listing falls back to flat behavior.
 *
 * The fetch side imports analyticsClient DYNAMICALLY (inside
 * loadClusterTree) to keep the module graph acyclic at init.
 */
import { onGenerationPinChange } from "./generationPin.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

// ILO_CLUSTER has no time field — the planner ignores the range — but
// Query.time_range is required, so send the default relative form.
const TREE_TIME_RANGE = { relative: { durationSeconds: String(30 * 86400) } };
// Far above any real cluster count (bisecting writes up to ~75 top-level
// nodes and hundreds of leaves). If a rebuild ever exceeds this, log — silent
// truncation would quietly orphan subtrees.
const TREE_LIMIT = 5000;

const EMPTY_TREE = Object.freeze({
    byId: new Map(),
    childrenOf: new Map(),
    roots: [],
    isHierShape: false,
});

// Pure: rows [{id, name, parentId, level}] → tree. Exported for tests
// and for any future consumer that already has the node rows.
export function buildClusterTree(rows) {
    const byId = new Map();
    for (const r of rows ?? []) {
        if (r?.id == null || r.id === "") continue;
        const id = String(r.id);
        // Outlier pseudo-ids (-1 and below) never participate in the
        // hierarchy — the ec joins filter cluster_id > 0 everywhere.
        if (Number(id) < 0) continue;
        byId.set(id, {
            id,
            name: r.name ? String(r.name) : id,
            parentId: r.parentId != null && r.parentId !== "" ? String(r.parentId) : null,
            level: Number(r.level) || 0,
        });
    }
    const childrenOf = new Map();
    for (const node of byId.values()) {
        // A parent pointer to a node we never saw (truncation, partial
        // rebuild) makes this node a root rather than an orphan.
        if (node.parentId == null || !byId.has(node.parentId)) continue;
        const arr = childrenOf.get(node.parentId) ?? [];
        arr.push(node.id);
        childrenOf.set(node.parentId, arr);
    }
    const roots = [...byId.values()]
        .filter((n) => n.parentId == null || !byId.has(n.parentId))
        .map((n) => n.id);
    // dashboard-legacy's isLeidenHierShape: an internal node at level 0.
    // Flat kmeans meta nodes sit at level 1 with leaf children at level 0,
    // so flat algorithms stay flat.
    const isHierShape = [...byId.values()].some(
        (n) => n.level === 0 && childrenOf.has(n.id),
    );
    return { byId, childrenOf, roots, isHierShape };
}

// Leaf descendants of `id` (a leaf returns itself). Cycle-guarded;
// an internal node with no resolvable children degrades to itself —
// same defensive fallback dashboard-legacy used.
export function leafDescendantIds(tree, id) {
    const target = String(id);
    const root = tree.byId.get(target);
    if (!root) return [target];
    const leaves = [];
    const seen = new Set();
    const stack = [target];
    while (stack.length) {
        const cur = stack.pop();
        if (seen.has(cur)) continue;
        seen.add(cur);
        const kids = tree.childrenOf.get(cur);
        if (!kids || kids.length === 0) {
            leaves.push(cur);
            continue;
        }
        for (const k of kids) stack.push(k);
    }
    // A pathological all-cycle subtree yields no leaves — degrade to
    // the node itself rather than returning an empty set (an empty
    // IN-list would corrupt the predicate it feeds).
    return leaves.length > 0 ? leaves : [target];
}

// ── Fetch + cache ──────────────────────────────────────────────────

let _treePromise = null;

export function loadClusterTree() {
    if (_treePromise) return _treePromise;
    const epoch = getAuthIdentityEpoch();
    const request = (async () => {
        // Dynamic import: analyticsClient statically imports this module
        // for the runQuery expansion guard (see module doc).
        const { runQuery, rowsToObjects } = await import("./analyticsClient.js");
        const rows = rowsToObjects(await runQuery({
            root: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER" },
            timeRange: TREE_TIME_RANGE,
            output: {
                dimensions: [
                    { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_ID" },      outputAlias: "id" },
                    { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" },    outputAlias: "name" },
                    { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_META_CLUSTER_ID" }, outputAlias: "parentId" },
                    { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_HIERARCHY_LEVEL" }, outputAlias: "level" },
                ],
            },
            limit: TREE_LIMIT,
        }));
        if (rows.length >= TREE_LIMIT) {
            console.warn(`[clusterTree] node listing hit the ${TREE_LIMIT}-row cap — subtrees may be orphaned.`);
        }
        return getAuthIdentityEpoch() === epoch ? buildClusterTree(rows) : EMPTY_TREE;
    })().catch((err) => {
        // Fail soft: expansion no-ops, listing falls back to flat.
        // Keep the rejected promise replaced so a transient analytics
        // hiccup doesn't poison the whole session — next caller retries.
        console.warn("[clusterTree] load failed; cluster hierarchy disabled for this attempt:", err);
        if (_treePromise === request) _treePromise = null;
        return EMPTY_TREE;
    });
    _treePromise = request;
    return request;
}

export function __setClusterTreeForTests(tree) {
    _treePromise = tree == null ? null : Promise.resolve(tree);
}

export function __clusterTreeCacheForTests() {
    return _treePromise;
}

// Drop the cached tree so the next loadClusterTree() refetches. Wired
// to the generation pin below: a different generation has a different
// cluster tree, and the cached one would otherwise persist for the
// whole session.
export function resetClusterTree() {
    _treePromise = null;
}

// Self-subscribe at import: analyticsClient statically imports this
// module, so the subscription is always registered in any session
// that can run a query. generationPin is a leaf module (no cycle).
onGenerationPinChange(resetClusterTree);
onAuthIdentityChange(resetClusterTree);
